import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Box,
  Button,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';

import {
  Container,
  AcessoComponentes,
} from '../../../../../shared/services/api/c4-model/SistemasService';
import { EnumContainerLinguagem } from '../../../../../shared/enums';
import { ModalContainers } from './ModalContainers';
import { TiposLinguagemService } from '../../../../../shared/services/api/c4-model/TiposLinguagemService';
import { ContainerService } from '../../../../../shared/services/api/c4-model/ContainerService';
import { Skeleton } from '@mui/material';

type TipoLinguagem = {
  id: number;
  nome: string;
};

type TipoContainer = {
  id: number;
  nome: string;
};

type Row = {
  id: string;
  tipo: number;
  linguagem: number;
  versao: string;
  acessoContainers?: any[];
  acessoComponentes?: AcessoComponentes[];
};

type Props = {
  initialRows?: Container[];
  onContainersChange?: (containers: Container[]) => void;
  isLoading?: boolean;
};

const convertToRows = (containers: Container[]): Row[] => {
  return containers.map((item) => ({
    id: item.id,
    tipo: item.tipo,
    linguagem: item.linguagem,
    versao: item.versao,
    acessoContainers: item.acessoContainers,
    acessoComponentes: item.acessoComponentes,
  }));
};

const convertToContainers = (rows: Row[]): Container[] => {
  return rows.map((row) => ({
    id: row.id,
    sistemaId: '', // será preenchido no backend
    tipo: row.tipo as number,
    linguagem: row.linguagem as number,
    versao: row.versao,
    acessoContainers: row.acessoContainers || [],
    acessoComponentes: row.acessoComponentes || [],
  }));
};

export const ListaContainers: React.FC<Props> = ({
  initialRows = [],
  onContainersChange,
  isLoading = false,
}) => {
  const [rows, setRows] = useState<Row[]>([]);
  const [openModal, setOpenModal] = useState(false);
  const [editingRow, setEditingRow] = useState<Row | null>(null);
  const [tiposLinguagem, setTiposLinguagem] = useState<TipoLinguagem[]>([]);
  const [tiposContainer, setTiposContainer] = useState<TipoContainer[]>([]);

  // Função para obter o nome da linguagem
  const getTipoLinguagemNome = (id: number): string => {
    const tipoLinguagem = tiposLinguagem.find((t) => t.id === id);
    return tipoLinguagem?.nome || `Linguagem ${id}`;
  };

  // Função para obter o nome do container
  const getTipoContainerNome = (id: number): string => {
    const tipoContainer = tiposContainer.find((t) => t.id === id);
    return tipoContainer?.nome || `Container ${id}`;
  };

  useEffect(() => {
    // Carregar tipos de linguagem
    TiposLinguagemService.getAll().then((data) => {
      if (Array.isArray(data)) {
        setTiposLinguagem(data);
      }
    });

    // Carregar tipos de container
    ContainerService.getAll().then((data) => {
      if (Array.isArray(data)) {
        setTiposContainer(data);
      }
    });
  }, []);

  useEffect(() => {
    if (initialRows.length > 0) {
      setRows(convertToRows(initialRows));
    } else {
      setRows([]);
    }
  }, [initialRows]);

  const handleEdit = (id: string) => {
    const row = rows.find((r) => r.id === id);
    if (!row) return;
    setEditingRow(row);
    setOpenModal(true);
  };

  const handleDelete = (id: string) => {
    const newRows = rows.filter((r) => r.id !== id);
    setRows(newRows);
    if (onContainersChange) {
      onContainersChange(convertToContainers(newRows));
    }
  };

  const handleOpen = () => {
    // Open modal for creating a new container with empty fields
    setEditingRow(null);
    setOpenModal(true);
  };
  const handleClose = () => setOpenModal(false);

  const handleSave = (values: any) => {
    let newRows: any[] = [];
    if (values.id) {
      newRows = rows.map((r) =>
        r.id === values.id
          ? {
            ...r,
            tipo: values.tipo ?? r.tipo,
            linguagem: values.linguagem ?? r.linguagem,
            versao: values.versao ?? r.versao,
            acessoContainers: values.acessoContainers ?? r.acessoContainers,
            acessoComponentes:
                values.acessoComponentes ?? r.acessoComponentes,
          }
          : r
      );
    } else {
      const newRow: Row = {
        id: String(Date.now()),
        tipo: values.tipo ?? 1, // EnumContainerTipo.FRONTEND
        linguagem: values.linguagem ?? EnumContainerLinguagem.REACTJS,
        versao: values.versao || '1.0.0',
        acessoContainers: values.acessoContainers || [],
        acessoComponentes: values.acessoComponentes || [],
      };
      newRows = [newRow, ...rows];
    }
    setRows(newRows);
    if (onContainersChange) {
      onContainersChange(convertToContainers(newRows));
    }
  };

  return (
    <Box display="flex" flexDirection="column" alignItems="flex-end" gap={4}>
      {isLoading ? (
        <Skeleton variant="rectangular" width={80} height={36} />
      ) : (
        <Button
          variant="contained"
          color="primary"
          sx={{ width: '80px' }}
          onClick={handleOpen}
          startIcon={<AddIcon />}
        >
          Novo
        </Button>
      )}
      <ModalContainers
        open={openModal}
        onClose={() => {
          handleClose();
          setEditingRow(null);
        }}
        onSave={(v: any) => {
          handleSave(v);
          handleClose();
          setEditingRow(null);
        }}
        initialValues={
          editingRow
            ? {
              id: editingRow.id,
              tipo: editingRow.tipo,
              linguagem: editingRow.linguagem,
              versao: editingRow.versao,
              acessoContainers: editingRow.acessoContainers,
              acessoComponentes: editingRow.acessoComponentes,
            }
            : undefined
        }
      />
      <TableContainer component={Paper} elevation={1}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={100}>Ações</TableCell>
              <TableCell>Tipo</TableCell>
              <TableCell>Linguagem</TableCell>
              <TableCell>Versão</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <>
                {[1, 2, 3].map((n) => (
                  <TableRow key={n}>
                    <TableCell>
                      <Skeleton variant="rectangular" width={80} height={30} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width={150} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width={150} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width={100} />
                    </TableCell>
                  </TableRow>
                ))}
              </>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={4}
                  align="center"
                  sx={{ py: 4, color: 'text.secondary' }}
                >
                  Nenhum container cadastrado
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>
                    <Tooltip title="Excluir">
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(row.id)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Editar">
                      <IconButton
                        size="small"
                        onClick={() => handleEdit(row.id)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                  <TableCell>{getTipoContainerNome(row.tipo)}</TableCell>
                  <TableCell>{getTipoLinguagemNome(row.linguagem)}</TableCell>
                  <TableCell>{row.versao}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
